import {
  calendarDayIn,
  instantOnDayMs,
} from '@/features/dashboards/daily-slot.js';
import type { TrackedTask } from '@/lib/api/tasks.js';

/**
 * Dates of the task tracker, read in the tracker's time zone: the snooze choices, whether a task is
 * overdue or due today, the due date field, and the few tasks to start the day with.
 */

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/** A snooze choice: its words and the instant the task wakes up. */
export interface SnoozeOption {
  label: string;
  until: number;
}

/** The calendar day `days` after `calendarDay`, both `YYYY-MM-DD`. */
function addDays(calendarDay: string, days: number): string {
  return new Date(Date.parse(`${calendarDay}T00:00:00Z`) + days * DAY_MS)
    .toISOString()
    .slice(0, 10);
}

/** The ISO weekday of a calendar day: 1 is Monday and 7 is Sunday. */
function isoWeekday(calendarDay: string): number {
  return new Date(`${calendarDay}T00:00:00Z`).getUTCDay() || 7;
}

/** Two hours from now, then tomorrow and next Monday when the working day starts. */
export function snoozeOptions(
  nowMs: number,
  workStart: string,
  timeZone: string,
): SnoozeOption[] {
  const today = calendarDayIn(nowMs, timeZone);
  const morningOf = (days: number): number =>
    instantOnDayMs(addDays(today, days), workStart, timeZone);
  return [
    { label: 'In 2 hours', until: nowMs + 2 * HOUR_MS },
    { label: 'Tomorrow morning', until: morningOf(1) },
    { label: 'Next Monday morning', until: morningOf(8 - isoWeekday(today)) },
  ];
}

/** Whether a task still waits for its owner: not Done and not dropped. */
export function isOpen(task: TrackedTask): boolean {
  return task.status !== 'done' && task.status !== 'wont_do';
}

const WEEK_MS = 7 * DAY_MS;

/** Whether a task was done in the 7 days before `nowMs`; one with no done date counts from when it came. */
export function doneThisWeek(task: TrackedTask, nowMs: number): boolean {
  return (
    task.status === 'done' &&
    nowMs - Date.parse(task.doneAt ?? task.createdAt) <= WEEK_MS
  );
}

/** How a due date stands today. Only an open task can be overdue. */
export type DueState = 'overdue' | 'today' | 'later';

export function dueState(
  task: TrackedTask,
  nowMs: number,
  timeZone: string,
): DueState | null {
  if (task.dueAt === null) return null;
  const dueMs = Date.parse(task.dueAt);
  if (!isOpen(task)) return 'later';
  if (dueMs < nowMs) return 'overdue';
  return calendarDayIn(dueMs, timeZone) === calendarDayIn(nowMs, timeZone)
    ? 'today'
    : 'later';
}

/** A day and a short month in the tracker's zone: `31 Oct`, or `Oct 31` in a US locale. */
export function formatDay(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat([], {
    day: 'numeric',
    month: 'short',
    timeZone,
  }).format(Date.parse(iso));
}

/** A due date in words: overdue since a day, due today, or due a day. */
export function dueLabel(
  state: DueState,
  dueAt: string,
  timeZone: string,
): string {
  if (state === 'today') return 'Due today';
  const day = formatDay(dueAt, timeZone);
  return state === 'overdue' ? `Overdue since ${day}` : `Due ${day}`;
}

/** The due day as a date field shows it, or empty when there is none. */
export function dueDateInput(dueAt: string | null, timeZone: string): string {
  return dueAt === null ? '' : calendarDayIn(Date.parse(dueAt), timeZone);
}

/** A picked day as the due instant: the end of that working day. An emptied field clears it. */
export function dueAtFromInput(
  calendarDay: string,
  workEnd: string,
  timeZone: string,
): string | null {
  if (calendarDay === '') return null;
  return new Date(instantOnDayMs(calendarDay, workEnd, timeZone)).toISOString();
}

/** Why a task is in today's focus. */
export type FocusReason = 'overdue' | 'today' | 'high' | 'oldest';

export interface FocusItem {
  task: TrackedTask;
  reason: FocusReason;
}

const FOCUS_SIZE = 3;
const REASON_ORDER: readonly FocusReason[] = [
  'overdue',
  'today',
  'high',
  'oldest',
];

function focusReason(
  task: TrackedTask,
  nowMs: number,
  timeZone: string,
): FocusReason {
  const due = dueState(task, nowMs, timeZone);
  if (due === 'overdue' || due === 'today') return due;
  return task.priority === 'high' ? 'high' : 'oldest';
}

/**
 * The three open tasks for the user to start with: overdue first, then due today, then high
 * priority, then the oldest, each kind oldest first. A snoozed task waits for its time and is left
 * out, and so is a task the user gave someone, which waits on them.
 */
export function focusForToday(
  tasks: readonly TrackedTask[],
  nowMs: number,
  timeZone: string,
): FocusItem[] {
  return tasks
    .filter(
      (task) =>
        isOpen(task) &&
        task.status !== 'postponed' &&
        task.direction !== 'from_me',
    )
    .map((task) => ({ task, reason: focusReason(task, nowMs, timeZone) }))
    .sort(
      (left, right) =>
        REASON_ORDER.indexOf(left.reason) -
          REASON_ORDER.indexOf(right.reason) ||
        Date.parse(left.task.createdAt) - Date.parse(right.task.createdAt),
    )
    .slice(0, FOCUS_SIZE);
}
